import * as React from 'react';
import { I18n, Translate } from 'react-redux-i18n';

import { InputWrapper } from '../styles';
import { PageTitle, PageInput } from './styles';
import LinkTypeToggle from './LinkTypeToggle';
import { LinkType } from '../../../../containers/Editor/constants';
import { LinkData, LinkPopupData } from '../../../../containers/Editor/utils/helpers';
import { Api } from '../../../../typings/api';
import { StoreState } from '../../../../reducers';
import { Dispatch } from 'react-redux';
import { getAspDriveItem } from '../../../../containers/DriveItemList/actions';
import DriveItemUtils from '../../../../utils/driveItem';
import ResourcePanel from '../ResourcePanel';
import LinkPanelInput, { linkDataUsingInput } from './LinkPanelInput';

interface LinkPanelProps {
	linkData: LinkData | null;
	onChange: (value: LinkData | null) => void;
	isImporting: boolean;
	spreads?: number[][];
	canAddLink?: boolean;
	canAddPopupAction?: boolean;
	isUrlLinkOnly?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
	dispatch: Dispatch<StoreState>;
}

const getTypeFromLinkData = (linkData: LinkData | null) => linkData ? linkData.type : LinkType.unsupported;

const LinkPanel = ({
	linkData,
	isImporting,
	spreads = [],
	canAddLink = true,
	canAddPopupAction = false,
	isUrlLinkOnly,
	onChange,
	onFocus,
	onBlur,
	dispatch,
}: LinkPanelProps) => {
	const initialType = getTypeFromLinkData(linkData);
	const [type, setType] = React.useState(initialType);
	const isPublicationPopup = linkData && linkData.type === LinkType.popup && linkData.data.item.type === Api.AssetItemFileTypes.PUBLICATION;
	let initialPublicationPage: string = '1';
	if (isPublicationPopup) {
		initialPublicationPage = (linkData as LinkPopupData).data.page!.toString();
	}
	const [publicationPage, setPublicationPage] = React.useState(initialPublicationPage);

	const handleTypeChange = (newType: LinkType) => {
		setType(newType);
	};

	const renderSettings = (isDriveResource: boolean) => {
		return isDriveResource ?
			renderDriveAssetSettings() :
			renderLinkSettings();
	};

	const renderLinkSettings = () => (
		<LinkPanelInput
			linkData={linkData as linkDataUsingInput}
			type={type}
			spreads={spreads}
			canAddLink={canAddLink}
			onChange={onChange}
			onFocus={onFocus}
			onBlur={onBlur}
		/>
	);

	const onPublicationPageChange = (page: string) => {
		setPublicationPage(page);
		const newLinkData = {
			...linkData as LinkPopupData,
		};
		const pageNumber = parseInt(page, 10);
		newLinkData.data.page = pageNumber ? pageNumber : 1;
		onChange(newLinkData);
	};

	const onResourceSelect = async (item: Api.DriveItem) => {
		const aspDriveItem = await dispatch(getAspDriveItem(item.id));
		const publicationPage = 1;
		setPublicationPage(publicationPage.toString());
		onChange({
			type: LinkType.popup,
			data: {
				item: aspDriveItem!,
				page: publicationPage,
			},
			originalParams: linkData && linkData.type === LinkType.popup && linkData.originalParams
				? linkData.originalParams
				: undefined,
		});
	};

	const renderPublicationPageInput = () => {
		return (
			<>
				<PageTitle><Translate value="messages.page"/></PageTitle>
				<PageInput onChange={(event) => onPublicationPageChange(event.currentTarget.value)} value={publicationPage} />
			</>
		);
	};

	const renderDriveAssetSettings = () => {
		const linkPopupData = linkData as LinkPopupData;

		const fileExtensions = [
			...DriveItemUtils.publicationExtensions,
			...DriveItemUtils.imageWithSvgExtensions,
			...DriveItemUtils.videoExtensions,
			...DriveItemUtils.componentExtensions,
		];

		const openableDriveItemTypes = [
			Api.DriveItemType.Component,
			Api.DriveItemType.Image,
			Api.DriveItemType.Movie,
			Api.DriveItemType.Publication,
		];

		const isPublication = linkPopupData.data.item && linkPopupData.data.item.type === Api.AssetItemFileTypes.PUBLICATION;

		return (
			<>
				<ResourcePanel
					iconName={'iconset_uprez_file'}
					label={linkPopupData.data.item ? linkPopupData.data.item.label : I18n.t('messages.no_target_selected')}
					isExternalLinkActive={false}
					importManagerId={'linkPanel'}
					isImporting={isImporting}
					isResetButtonDisplayed={false}
					isValidResourceFromApp={(driveItem: Api.DriveItem) => openableDriveItemTypes.includes(driveItem.type)}
					fileExtensions={fileExtensions}
					isFileExtensionsDisplayed={true}
					isOptimizedImageImport={false}
					fileType={undefined}// TODO: upload of component may need to be handled
					onResourceSelect={onResourceSelect}
					dispatch={dispatch}
				/>
				{
					isPublication && renderPublicationPageInput()
				}
			</>
		);
	};

	React.useEffect(() => {
		// update state when value from props was changed (when switch between links)
		const type = getTypeFromLinkData(linkData);
		setType(type);
	}, [linkData]);

	return (
		<>
			<InputWrapper
				hasButtonAdd={true}
				hasButtonRemove={true}
			>
				{renderSettings(type === LinkType.popup)}
			</InputWrapper>
			{!isUrlLinkOnly &&
				<LinkTypeToggle
					canAddPopupAction={canAddPopupAction}
					type={type}
					onChange={handleTypeChange}
				/>
			}
		</>
	);
};

export default LinkPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/LinkPanel/index.tsx